import {
    PatientAggregateStatsType,
    StatisticItemKey,
    StatisticSectionKey,
} from '~/components/Statistics';
import { SearchValuesType } from '~/services';
import {
    enumValue,
    getGapTypeBySection,
    getGapStatusRollups,
    isInRolledUpStatus,
} from '~/util/enums';
import { prepareSearchQueryString } from '~/util/search';

type GapType = 'risk_gap' | 'foc_gap' | 'quality_gap';

export function prepareStatsLink(
    fields: Partial<SearchValuesType>,
    section: StatisticSectionKey,
    item: StatisticItemKey
) {
    let link = prepareSearchQueryString(fields);

    if (section === 'members') return link;
    if (item === 'in_progress') return `${link}&exists[gap_totals.oldest_answer_at]=true`;

    link += `&${section}.status[]=`;
    const gapType = getGapTypeBySection(section);
    link += getGapStatusRollups(gapType, item).join(`&${section}.status[]=`);
    return link;
}

export function rollUpStatStatuses(
    result: {
        data: PatientAggregateStatsType[];
    },
    includeFOC = true
): PatientAggregateStatsType {
    // Init/roll-up risk gaps data
    let addressedIdx = -1;
    let failIdx = -1;
    const riskGaps = result.data[0].risk_gaps;

    // Roll-up "Addressed" and "Fail" statuses into a single count for risk gap
    let addressedCount = 0;
    let failCount = 0;
    riskGaps.forEach((y, idx) => {
        if (y.status === enumValue(['gap', 'status', 'Addressed'])) {
            addressedIdx = idx;
        }
        if (y.status === enumValue(['gap', 'status', 'Fail'])) {
            failIdx = idx;
        }

        if (
            isInRolledUpStatus(
                enumValue(['gap', 'type', 'Risk Gap']),
                enumValue(['gap', 'status', 'Addressed']),
                y.status
            )
        ) {
            addressedCount += y.count as number;
        }
        if (
            isInRolledUpStatus(
                enumValue(['gap', 'type', 'Risk Gap']),
                enumValue(['gap', 'status', 'Fail']),
                y.status
            )
        ) {
            failCount += y.count as number;
        }
    });

    const countTotal = riskGaps.reduce((acc, x) => (x.count as number) + acc, 0);
    const percent = (addressedCount / countTotal) * 100;
    // Add rolled-up "Addressed" count
    riskGaps[addressedIdx].count = addressedCount;
    riskGaps[failIdx].count = failCount;
    // Add percentage
    riskGaps.push({
        name: 'Percent',
        status: 'percent',
        count: countTotal === 0 ? '0%' : `${Math.round(percent)}%`,
    });
    result.data[0].risk_gaps = riskGaps;

    // Roll-up "Addressed" and "Fail" statuses into a single count for quality gaps
    const qualityGaps = result.data[0].quality_gaps;
    addressedCount = 0;
    failCount = 0;
    let pendingCount = 0;
    let pendingIdx = 0;
    qualityGaps.forEach((y, idx) => {
        if (y.status === enumValue(['gap', 'status', 'Addressed'])) {
            addressedIdx = idx;
        }
        if (y.status === enumValue(['gap', 'status', 'Fail'])) {
            failIdx = idx;
        }
        if (y.status === enumValue(['gap', 'status', 'Pending'])) {
            pendingIdx = idx;
        }

        if (
            isInRolledUpStatus(
                enumValue(['gap', 'type', 'Quality Gap']),
                enumValue(['gap', 'status', 'Pending']),
                y.status
            )
        ) {
            pendingCount += y.count as number;
        }
        if (
            isInRolledUpStatus(
                enumValue(['gap', 'type', 'Quality Gap']),
                enumValue(['gap', 'status', 'Addressed']),
                y.status
            )
        ) {
            addressedCount += y.count as number;
        }
        if (
            isInRolledUpStatus(
                enumValue(['gap', 'type', 'Quality Gap']),
                enumValue(['gap', 'status', 'Fail']),
                y.status
            )
        ) {
            failCount += y.count as number;
        }
    });

    // Add rolled-up "Addressed" count
    qualityGaps[addressedIdx].count = addressedCount;
    qualityGaps[failIdx].count = failCount;
    qualityGaps[pendingIdx].count = pendingCount;
    result.data[0].quality_gaps = qualityGaps;

    // Add summed in progress gap count
    const categories = ['risk_gap', 'quality_gap'];
    if (includeFOC) categories.push('foc_gap');
    result.data[0].gap_totals.ready_with_answer = categories.reduce(
        (cur, x: GapType) => cur + result.data[0].gap_totals[x].ready_with_answer,
        0
    );

    return result.data[0];
}
